export const FULFILLMENT_EVENT_TYPES = {
  LABEL_SCAN: 'label_scan',
  LABEL_SCAN_FAILED: 'label_scan_failed',
  ORDER_SUBMIT: 'order_submit',
  ORDER_ASSIGNED: 'order_assigned',
  ORDER_RELEASED: 'order_released',
  LABEL_PURCHASED: 'label_purchased',
  CLEARED_FOR_FULFILLMENT: 'cleared_for_fulfillment',
  CLEARANCE_REMOVED: 'clearance_removed',
} as const;

export type FulfillmentEventType = (typeof FULFILLMENT_EVENT_TYPES)[keyof typeof FULFILLMENT_EVENT_TYPES];

/** Event types the client may submit via POST /api/fulfillment-events. All others are server-emitted only. */
export const CLIENT_FULFILLMENT_EVENT_TYPES = {
  LABEL_SCAN: FULFILLMENT_EVENT_TYPES.LABEL_SCAN,
  LABEL_SCAN_FAILED: FULFILLMENT_EVENT_TYPES.LABEL_SCAN_FAILED,
  ORDER_SUBMIT: FULFILLMENT_EVENT_TYPES.ORDER_SUBMIT,
} as const;

export type ClientFulfillmentEventType =
  (typeof CLIENT_FULFILLMENT_EVENT_TYPES)[keyof typeof CLIENT_FULFILLMENT_EVENT_TYPES];

export const FULFILLMENT_EVENT_SOURCES = {
  WMS_APP: 'wms_app',
  SYSTEM: 'system',
  SHIPSTATION_WEBHOOK: 'shipstation_webhook',
  SHOPIFY_WEBHOOK: 'shopify_webhook',
} as const;

export type FulfillmentEventSource = (typeof FULFILLMENT_EVENT_SOURCES)[keyof typeof FULFILLMENT_EVENT_SOURCES];

export const FULFILLMENT_EVENT_ATTRIBUTIONS = {
  DIRECT: 'direct',
  INFERRED: 'inferred',
  SYSTEM: 'system',
} as const;

export type FulfillmentEventAttribution =
  (typeof FULFILLMENT_EVENT_ATTRIBUTIONS)[keyof typeof FULFILLMENT_EVENT_ATTRIBUTIONS];

export const SLA_CLASSES = {
  STANDARD: 'standard',
  EXPEDITED: 'expedited',
} as const;

export type SlaClass = (typeof SLA_CLASSES)[keyof typeof SLA_CLASSES];

export const SCAN_FAILURE_REASONS = {
  NO_MATCH: 'no_match',
  NO_SKU_DETECTED: 'no_sku_detected',
  OCR_ERROR: 'ocr_error',
} as const;

export type ScanFailureReason = (typeof SCAN_FAILURE_REASONS)[keyof typeof SCAN_FAILURE_REASONS];

export const ORDER_RELEASE_REASONS = {
  TESTING: 'testing',
  OUT_OF_STOCK: 'out_of_stock',
  OTHER: 'other',
} as const;

export type OrderReleaseReason = (typeof ORDER_RELEASE_REASONS)[keyof typeof ORDER_RELEASE_REASONS];

export const ASSIGNMENT_SOURCES = {
  PICK_NEXT: 'pick_next',
  BATCH_ORDERS: 'batch_orders',
  CLAIM_SCAN: 'claim_scan',
} as const;

export type AssignmentSource = (typeof ASSIGNMENT_SOURCES)[keyof typeof ASSIGNMENT_SOURCES];

/** Public Realtime Broadcast channel — not postgres_changes on fulfillment_events. */
export const ORDER_ASSIGNMENT_REALTIME_CHANNEL = 'wms-order-assignments' as const;
export const ORDER_ASSIGNED_BROADCAST_EVENT = 'order_assigned' as const;

/** Per-event-type metadata payloads. Keep flat and JSON-serializable. */
export interface LabelScanMetadata {
  readonly sku?: string;
}

/** Failed OCR barcode scan: OCR read text that did not match an unscanned order line. */
export interface LabelScanFailedMetadata {
  readonly reason: ScanFailureReason;
  readonly ocr_text?: string;
}

export interface OrderSubmitMetadata {
  readonly line_count?: number;
  readonly unit_count?: number;
}

export interface OrderAssignedMetadata {
  readonly assignment_source: AssignmentSource;
  readonly user_tag_id?: number;
}

/** Compensating event for pick-next: order returned to queue or parked on Hold for CS. */
export interface OrderReleasedMetadata {
  readonly reason: OrderReleaseReason;
  readonly user_tag_id?: number;
}

/** Emitted from the ShipStation SHIP_NOTIFY webhook; shipment id doubles as the dedupe key. */
export interface LabelPurchasedMetadata {
  readonly shipstation_shipment_id: string;
  readonly tracking_number?: string;
  readonly carrier_code?: string;
  readonly ship_date?: string;
  readonly backfilled_from?: 'shipstation_reconcile' | 'shipstation_merge' | 'shipstation_successor';
  /** Survivor orderId when this label was mirrored from a ShipStation merge. */
  readonly merged_into?: string;
  /** Live orderId when this label was mirrored onto a superseded ShipStation row. */
  readonly succeeded_by?: string;
}

/**
 * Opens a clearance episode. queue_date/sla_class are stamped at clear time so
 * the SLA RPC never re-derives business-day rules from moving data.
 */
export interface ClearedForFulfillmentMetadata {
  readonly order_number: string;
  readonly queue_date: string;
  readonly sla_class: SlaClass;
  readonly reason?: 'restored';
}

/** Voids the open clearance episode (unless the order already shipped). */
export interface ClearanceRemovedMetadata {
  readonly order_number: string;
  readonly reason?: 'cancelled' | 'deleted' | 'replaced';
}

export type FulfillmentEventMetadata =
  | LabelScanMetadata
  | LabelScanFailedMetadata
  | OrderSubmitMetadata
  | OrderAssignedMetadata
  | OrderReleasedMetadata
  | LabelPurchasedMetadata
  | ClearedForFulfillmentMetadata
  | ClearanceRemovedMetadata;

export interface FulfillmentEventInput {
  readonly eventType: ClientFulfillmentEventType;
  readonly orderId: string;
  readonly metadata?: FulfillmentEventMetadata;
}
